"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.makeApiClient = void 0;
const RegisterBody_1 = require("./model/RegisterBody");
const LoginBody_1 = require("./model/LoginBody");
const LegacyLoginBody_1 = require("./model/LegacyLoginBody");
const LoginResult_1 = require("./model/LoginResult");
const ApiHttpClient_1 = require("./ApiHttpClient");
const RegisterResult_1 = require("./model/RegisterResult");
const CreateDeckBody_1 = require("./model/CreateDeckBody");
const DeckResult_1 = require("./model/DeckResult");
const GetDecksResult_1 = require("./model/GetDecksResult");
const HttpClient_1 = require("./util/HttpClient");
const UpdateDeckBody_1 = require("./model/UpdateDeckBody");
const BeginImageUploadBody_1 = require("./model/BeginImageUploadBody");
const BeginImageUploadResult_1 = require("./model/BeginImageUploadResult");
const BeginPasswordResetBody_1 = require("./model/BeginPasswordResetBody");
const CompletePasswordResetBody_1 = require("./model/CompletePasswordResetBody");
const RefreshTokenBody_1 = require("./model/RefreshTokenBody");
const ApiCredentialService_1 = require("./ApiCredentialService");
const HttpValueCollection_1 = require("./util/HttpValueCollection");
const trace_1 = require("./util/trace");
const ApiAwsCredentials_1 = require("./ApiAwsCredentials");
const PresentingSessionResult_1 = require("./model/PresentingSessionResult");
const CreatePresentingSessionBody_1 = require("./model/CreatePresentingSessionBody");
const AccessCodeBody_1 = require("./model/AccessCodeBody");
const RecoveryResult_1 = require("./model/RecoveryResult");
const PasswordRecoveryResult_1 = require("./model/PasswordRecoveryResult");
const CreateCommsSessionBody_1 = require("./model/CreateCommsSessionBody");
const CommsSessionResult_1 = require("./model/CommsSessionResult");
const trace = (0, trace_1.makeTrace)('api');
function makeApiClient(cfg) {
    const client = (0, ApiHttpClient_1.makeBaseClient)(cfg.fetch);
    const baseUrl = cfg.baseUrl.endsWith('/') ? cfg.baseUrl : cfg.baseUrl + '/';
    //TODO This is currently hard coded for production as baseUrl is pulled from elsewhere and doesn't tie in properly (ref: cdk.ts) - Needs looked at as bigger piece of work 22/12/21 - KM
    const portalUrl = 'https://portal.presentpal.co.uk';
    const creds = makeCreds(refreshToken, cfg.credentials);
    async function loggedInUser() {
        const auth = await creds();
        if (!auth) {
            throw new Error(`expected logged in user`);
        }
        return auth.sub;
    }
    const authenticatedClient = async (request, validateResult, validateRequest) => {
        var _a;
        // retry on auth failure
        for (let i = 0;; ++i) {
            // force refresh of creds on second attempt
            const accessToken = (_a = (await creds(i > 0))) === null || _a === void 0 ? void 0 : _a.accessToken;
            const response = await client(accessToken
                ? { ...request, headers: withAuth(accessToken, request.headers) }
                : request, validateResult, validateRequest);
            if (response.status !== 401 || i > 0) {
                return response;
            }
        }
    };
    async function beginImageUpload(accountId, body) {
        accountId = accountId || (await loggedInUser());
        return await authenticatedClient({
            method: 'POST',
            url: baseUrl + `accounts/${accountId}/uploads`,
            body,
        }, BeginImageUploadResult_1.validateBeginImageUploadResult, BeginImageUploadBody_1.validateBeginImageUploadBody);
    }
    async function beginPasswordReset(email) {
        return await client({
            method: 'POST',
            url: baseUrl + `auth/reset/begin`,
            body: { email },
        }, undefined, BeginPasswordResetBody_1.validateBeginPasswordResetBody);
    }
    async function createPresentingSession(accountId, description) {
        accountId = accountId || (await loggedInUser());
        return await authenticatedClient({
            method: 'POST',
            url: baseUrl + `accounts/${accountId}/sessions`,
            body: { description },
        }, PresentingSessionResult_1.validatePresentingSessionResult, CreatePresentingSessionBody_1.validateCreatePresentingSessionBody);
    }
    async function createCommsSession(accountId, description) {
        accountId = accountId || (await loggedInUser());
        return await authenticatedClient({
            method: 'POST',
            url: baseUrl + `accounts/${accountId}/comms-sessions`,
            body: { description },
        }, CommsSessionResult_1.validateCommsSessionResult, CreateCommsSessionBody_1.validateCreateCommsSessionBody);
    }
    async function completePasswordReset(body) {
        return await client({
            method: 'POST',
            url: baseUrl + `auth/reset/complete`,
            body,
        }, undefined, CompletePasswordResetBody_1.validateCompletePasswordResetBody);
    }
    async function createDeck(accountId, deck) {
        accountId = accountId || (await loggedInUser());
        return await authenticatedClient({
            method: 'POST',
            url: baseUrl + `accounts/${accountId}/decks`,
            body: deck,
        }, DeckResult_1.validateDeckResult, CreateDeckBody_1.validateCreateDeckBody);
    }
    async function currentAuth() {
        return creds();
    }
    async function getAwsCredentials() {
        const current = await creds();
        if (!current) {
            return;
        }
        return new ApiAwsCredentials_1.ApiAwsCredentials(current, () => creds(true));
    }
    async function getDecks(accountId) {
        accountId = accountId || (await loggedInUser());
        return await authenticatedClient({
            method: 'GET',
            url: baseUrl + `accounts/${accountId}/decks`,
        }, GetDecksResult_1.validateGetDecksResult);
    }
    async function login(body) {
        const iat = Date.now();
        const response = await client({
            method: 'POST',
            body,
            url: baseUrl + `auth/login`,
        }, LoginResult_1.validateLoginResult, LoginBody_1.validateLoginBody);
        cfg.credentials.set(response.ok ? (0, ApiCredentialService_1.fromLoginResult)(response.result, iat) : undefined);
        return response;
    }
    async function legacyLogin(body) {
        const iat = Date.now();
        const response = await client({
            method: 'POST',
            body,
            url: baseUrl + `auth/login/legacy`,
        }, LoginResult_1.validateLoginResult, LegacyLoginBody_1.validateLegacyLoginBody);
        cfg.credentials.set(response.ok ? (0, ApiCredentialService_1.fromLoginResult)(response.result, iat) : undefined);
        return response;
    }
    async function sendEventData(eventId, eventJson, portalAuthToken) {
        let result = false;
        try {
            const response = await client({
                method: 'POST',
                url: `${portalUrl}/api/v1/event/${eventId}`,
                body: eventJson,
                headers: {
                    'content-type': HttpClient_1.HttpContentType.Json,
                    Authorization: `Bearer ${portalAuthToken}`,
                },
            });
            if (!(0, HttpClient_1.httpOk)(response.status)) {
                throw new Error(`LicenceClient: received unexpected status ${response.status}`);
            }
            result = true;
        }
        catch (e) {
            result = false;
        }
        return result;
    }
    async function refreshToken(body) {
        const response = await client({
            method: 'POST',
            body,
            url: baseUrl + `auth/refresh`,
        }, LoginResult_1.validateLoginResult, RefreshTokenBody_1.validateRefreshTokenBody);
        return response;
    }
    async function register(body) {
        return await client({
            method: 'POST',
            body,
            url: baseUrl + `auth/register`,
        }, RegisterResult_1.validateRegisterResult, RegisterBody_1.validateRegisterBody);
    }
    async function resumePresentingSession(accountId, sessionId) {
        accountId = accountId || (await loggedInUser());
        return await authenticatedClient({
            method: 'POST',
            url: baseUrl + `accounts/${accountId}/sessions/${sessionId}`,
        }, PresentingSessionResult_1.validatePresentingSessionResult);
    }
    async function updateDeck(accountId, deckId, body) {
        accountId = accountId || (await loggedInUser());
        return await authenticatedClient({
            method: 'PUT',
            body,
            url: baseUrl + `accounts/${accountId}/decks/${deckId}`,
        }, DeckResult_1.validateDeckResult, UpdateDeckBody_1.validateUpdateDeckBody);
    }
    async function requestPassword(body) {
        return await client({
            method: 'POST',
            body,
            url: 'https://portal.presentpal.co.uk/api/v1/reset',
        }, PasswordRecoveryResult_1.validatePasswordRecoveryResult, AccessCodeBody_1.validateAccessCodeBody);
    }
    async function requestAccessCode(body) {
        return await client({
            method: 'POST',
            body,
            url: baseUrl + `auth/login/recover`,
        }, RecoveryResult_1.validateRecoveryResult, AccessCodeBody_1.validateAccessCodeBody);
    }
    return {
        beginImageUpload,
        beginPasswordReset,
        completePasswordReset,
        createDeck,
        createPresentingSession,
        createCommsSession,
        currentAuth,
        getAwsCredentials,
        getDecks,
        login,
        legacyLogin,
        sendEventData,
        refreshToken,
        register,
        resumePresentingSession,
        updateDeck,
        requestAccessCode,
        requestPassword,
    };
}
exports.makeApiClient = makeApiClient;
function withAuth(token, headers) {
    return (0, HttpValueCollection_1.setValue)(headers || {}, 'Authorization', `Bearer ${token}`);
}
function makeCreds(refresh, credentialService) {
    let authLoading;
    let authRefreshing = false;
    async function currentAuthNotLocked(forceRefresh = false) {
        var _a;
        trace(`currentAuthNotLocked called (forceRefresh=%o)`, forceRefresh);
        authRefreshing = false;
        const now = Date.now();
        const oldCreds = credentialService.get();
        let creds = oldCreds;
        trace(`creds %O`, creds);
        if (creds) {
            trace(`now is %s; expires at %s`, new Date().toISOString(), new Date(creds.expiresAt).toISOString());
        }
        if (!creds || creds.expiresAt < now || forceRefresh) {
            trace(`renewing auth`);
            authRefreshing = true;
            const refreshToken = (creds === null || creds === void 0 ? void 0 : creds.refreshToken) || ((_a = credentialService.refresh()) === null || _a === void 0 ? void 0 : _a.refreshToken);
            if (refreshToken) {
                trace(`renewing via refresh token`);
                const response = await refresh({
                    refreshToken,
                });
                if (response.ok) {
                    // copy over refresh token if we don't get a new one
                    if (!response.result.refreshToken && (oldCreds === null || oldCreds === void 0 ? void 0 : oldCreds.refreshToken)) {
                        response.result.refreshToken = oldCreds.refreshToken;
                    }
                    creds = (0, ApiCredentialService_1.fromLoginResult)(response.result, now);
                }
                else {
                    creds = undefined;
                }
            }
            else {
                trace(`no available method to renew`);
                creds = undefined;
            }
        }
        if (creds !== oldCreds) {
            trace(`persisting new credentials %O`, creds);
            credentialService.set(creds);
        }
        return creds;
    }
    async function currentAuth(forceRefresh = false) {
        trace(`currentAuth called %o`, {
            forceRefresh,
            authLoading: !!authLoading,
            authRefreshing,
        });
        if (authLoading && (!forceRefresh || authRefreshing)) {
            trace(`using in-progress operation`);
            return authLoading;
        }
        if (authLoading) {
            trace(`waiting for current operation`);
            // wait for existing auth but discard result
            await authLoading;
        }
        try {
            trace(`starting new operation`);
            authLoading = currentAuthNotLocked(forceRefresh);
            return await authLoading;
        }
        finally {
            trace(`ending operation`);
            authLoading = undefined;
        }
    }
    return currentAuth;
}
//# sourceMappingURL=ApiClient.js.map